// Reporte de Horas para Cálculo de Planilla (formato del reporte de VFP):
// una fila por colaborador con las horas de cada día (normales / extra) y los
// totales de la semana según el cierre (ordinarias, extra, gratificación,
// subsidio de incapacidad y devengado). Se abre en una ventana para imprimir.
// Mismo archivo en Empacadora (planta) y Aplicaciones (finca).

export interface DiaReporte { fecha: string; etiqueta: string }        // ej. { '2026-09-25', 'Viernes' }
export interface CeldaDia { nor: number; ext: number; marca?: string; prod?: number } // marca: 'Inc', 'S/E', 'Aus'…; prod: horas en labores por producción (informativas)
export interface FilaReporte {
  codigo: string; nombre: string;
  dias: Record<string, CeldaDia>;                 // por fecha ISO
  normal: number; extra: number; gratif: number;  // horas de la semana (cierre)
  salNormal: number; salExtra: number; salGratif: number; incap: number; devengado: number;
  nota?: string;
}

const fmtH = (n: number) => {
  const v = Number(n || 0);
  if (!v) return '';
  const h = Math.floor(v + 1e-9);
  const m = Math.round((v - h) * 60);
  return m === 60 ? `${h + 1}:00` : `${h}:${String(m).padStart(2, '0')}`;
};
const fmtM = (n: number) => (Number(n || 0) ? Number(n).toLocaleString('es-CR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '');
const fmtF = (s: string) => { const [y, m, d] = s.split('-'); return `${d}/${m}/${y}`; };
const esc = (s: string) => String(s ?? '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c] as string));

export function abrirReporteHorasSemana(p: {
  empresa: string; grupo: string; desde: string; hasta: string; fechaPlanilla: string;
  estado: string; dias: DiaReporte[]; filas: FilaReporte[];
  ventana?: Window | null;   // abierta en el clic (evita el bloqueo de ventanas emergentes)
}) {
  const filas = [...p.filas].sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
  const sum = (f: (r: FilaReporte) => number) => filas.reduce((s, r) => s + (Number(f(r)) || 0), 0);
  const hayGratif = sum(r => r.salGratif) > 0;
  const hayIncap = sum(r => r.incap) > 0;
  const prodDe = (r: FilaReporte) => Object.values(r.dias).reduce((s, c) => s + (Number(c.prod) || 0), 0);
  const hayProd = sum(prodDe) > 0;

  const celdasDia = (r: FilaReporte) => p.dias.map(d => {
    const c = r.dias[d.fecha];
    if (!c) return '<td class="d"></td><td class="d"></td>';
    const nor = (c.marca && !c.nor ? `<span class="m">${esc(c.marca)}</span>` : fmtH(c.nor))
      + (c.prod ? `<div class="p">P ${fmtH(c.prod)}</div>` : '');
    return `<td class="d">${nor}</td><td class="d">${fmtH(c.ext)}</td>`;
  }).join('');

  const cuerpo = filas.map(r => `<tr>
    <td class="emp">${esc(r.codigo)} ${esc(r.nombre)}${r.nota ? `<div class="nota">${esc(r.nota)}</div>` : ''}</td>
    ${celdasDia(r)}
    <td class="t">${fmtH(r.normal)}</td><td class="t">${fmtH(r.extra)}</td>${hayGratif ? `<td class="t">${fmtH(r.gratif)}</td>` : ''}${hayProd ? `<td class="t p">${fmtH(prodDe(r))}</td>` : ''}
    <td class="n">${fmtM(r.salNormal)}</td><td class="n">${fmtM(r.salExtra)}</td>${hayGratif ? `<td class="n">${fmtM(r.salGratif)}</td>` : ''}
    ${hayIncap ? `<td class="n">${fmtM(r.incap)}</td>` : ''}
    <td class="n b">${fmtM(r.devengado)}</td>
  </tr>`).join('');

  const totDias = p.dias.map(d => `<td class="d b">${fmtH(sum(r => r.dias[d.fecha]?.nor ?? 0))}</td><td class="d b">${fmtH(sum(r => r.dias[d.fecha]?.ext ?? 0))}</td>`).join('');
  const totDevengado = sum(r => r.devengado);

  const html = `<!DOCTYPE html><html lang="es"><head><meta charset="UTF-8"><title>Reporte de Horas ${esc(p.grupo)} ${fmtF(p.desde)}</title>
<style>
@page { size: Letter landscape; margin: 8mm 8mm; }
body { font-family: "Courier New", monospace; font-size: 9.5px; color: #111; margin: 0; }
.h1 { text-align:center; font-size: 15px; font-weight: 700; color:#7f1d1d; letter-spacing: .5px; }
.h2 { text-align:center; font-size: 11px; font-weight: 700; font-style: italic; color:#15803d; margin-top: 3px; }
.h3 { display:flex; justify-content: space-between; font-style: italic; font-size: 10px; margin: 4px 0 6px; }
table { border-collapse: collapse; width: 100%; }
th, td { border: 1px solid #555; padding: 2px 3px; }
th { background: #eee; font-weight: 600; font-size: 9px; }
td.emp { white-space: nowrap; font-size: 9px; }
td.d { text-align: right; width: 30px; font-size: 8.5px; }
td.t { text-align: right; width: 38px; }
td.n { text-align: right; width: 70px; }
.b { font-weight: 700; }
.m { color: #b45309; font-weight: 700; }
.nota { color:#555; font-size: 8px; }
.p { color:#1d4ed8; font-size: 8px; }
tfoot td { background: #f3f4f6; }
.total { display:flex; justify-content:flex-end; align-items:center; gap: 12px; margin-top: 8px; font-weight: 700; font-style: italic; font-size: 11px; }
.total span.v { border: 2px solid #111; padding: 3px 10px; min-width: 110px; text-align: right; font-style: normal; }
.pie { margin-top: 6px; font-size: 8.5px; color:#444; }
@media print { .noprint { display:none; } }
</style></head><body>
<div class="noprint" style="text-align:right;margin:6px"><button onclick="window.print()">Imprimir</button></div>
<div class="h1">${esc(p.empresa)}</div>
<div class="h2">Reporte de Horas para Cálculo de Planilla del: ${fmtF(p.desde)} al: ${fmtF(p.hasta)}</div>
<div class="h3"><span>Planilla del ${fmtF(p.fechaPlanilla)} · ${esc(p.estado)}</span><span>${esc(p.grupo)}</span><span>${filas.length} colaborador(es)</span></div>
<table>
<thead>
<tr><th rowspan="2">Empleado</th>${p.dias.map(d => `<th colspan="2">${esc(d.etiqueta)}<br><span style="font-weight:400">${fmtF(d.fecha).slice(0, 5)}</span></th>`).join('')}
<th rowspan="2">Normal</th><th rowspan="2">Extras</th>${hayGratif ? '<th rowspan="2">Gratif.</th>' : ''}${hayProd ? '<th rowspan="2">H. Prod.</th>' : ''}
<th rowspan="2">Salario/Norl</th><th rowspan="2">Salario/Ext</th>${hayGratif ? '<th rowspan="2">Gratificación</th>' : ''}
${hayIncap ? '<th rowspan="2">Subsidio Incap.</th>' : ''}<th rowspan="2">Devengado</th></tr>
<tr>${p.dias.map(() => '<th>Nor</th><th>Ext</th>').join('')}</tr>
</thead>
<tbody>${cuerpo}</tbody>
<tfoot><tr>
  <td class="b">TOTALES DE LA SEMANA (${filas.length})</td>${totDias}
  <td class="t b">${fmtH(sum(r => r.normal))}</td><td class="t b">${fmtH(sum(r => r.extra))}</td>${hayGratif ? `<td class="t b">${fmtH(sum(r => r.gratif))}</td>` : ''}${hayProd ? `<td class="t b p">${fmtH(sum(prodDe))}</td>` : ''}
  <td class="n b">${fmtM(sum(r => r.salNormal))}</td><td class="n b">${fmtM(sum(r => r.salExtra))}</td>${hayGratif ? `<td class="n b">${fmtM(sum(r => r.salGratif))}</td>` : ''}
  ${hayIncap ? `<td class="n b">${fmtM(sum(r => r.incap))}</td>` : ''}
  <td class="n b">${fmtM(totDevengado)}</td>
</tr></tfoot>
</table>
<div class="total"><span>Total Planilla</span><span class="v">${fmtM(totDevengado)}</span></div>
<div class="pie">Horas en formato horas:minutos. Por día: horas trabajadas (Nor / Ext). Totales de la semana según el cierre: Normal, Extras y Gratificación con sus montos; el subsidio de incapacidad no es salario. Inc = incapacidad · S/E = sin empaque · Aus = ausente · Per = permiso · Vac = vacaciones.${hayProd ? ' P / H. Prod. = horas en labores pagadas por producción: informativas, no entran en Normal / Extras ni en los montos.' : ''}</div>
</body></html>`;

  const w = p.ventana ?? window.open('', '_blank');
  if (!w) return false;
  w.document.open();
  w.document.write(html);
  w.document.close();
  return true;
}
